import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useMemo } from "react";
import { Picker } from "@design-system/Picker/Picker.lib";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import { useCloseStageSelectFooter } from "../CloseStageSelectFooter";
import {
    CloseTemplateTaskCallbacks,
    CloseTemplateTaskMeta,
    CloseTemplateTaskRow,
} from "../closeTemplateTasksViewConfig";

type Meta = ViewTableMeta<CloseTemplateTaskCallbacks, never> &
    Partial<CloseTemplateTaskMeta>;

export const CloseTemplateTaskCloseStageCell = memo(
    function CloseTemplateTaskCloseStageCell({
        row,
        table,
    }: CellContext<ViewTableFeatures, CloseTemplateTaskRow, string | null>) {
        const meta = (table.options.meta ?? {}) as Meta;
        const item = row.original;
        const sortedStagesRaw = meta.sortedStages;
        const sortedStages = useMemo(
            () => sortedStagesRaw ?? [],
            [sortedStagesRaw],
        );
        const renderStageFooter = useCloseStageSelectFooter();

        const stageName = useMemo(
            () =>
                item.closeStageId != null
                    ? (sortedStages.find((s) => s.id === item.closeStageId)
                          ?.name ?? null)
                    : null,
            [item.closeStageId, sortedStages],
        );

        if (item.isGroupRow) {
            return null;
        }

        return (
            <ViewTableCell>
                <Picker.Select<string>
                    value={stageName !== null ? item.closeStageId : null}
                    onValueChange={(value) =>
                        meta.callbacks?.onUpdateCloseStage(item.id, value)
                    }
                >
                    <Picker.CellContent value={stageName} addText="Set stage" />
                    <Picker.Popup>
                        <Picker.List>
                            <Picker.Item
                                onSelect={() => {
                                    if (item.closeStageId != null) {
                                        meta.callbacks?.onUpdateCloseStage(
                                            item.id,
                                            null,
                                        );
                                    }
                                }}
                            >
                                No stage
                            </Picker.Item>
                            {sortedStages.map((stage) => (
                                <Picker.Item key={stage.id} value={stage.id}>
                                    {stage.name}
                                </Picker.Item>
                            ))}
                            {renderStageFooter && (
                                <Picker.Footer>
                                    {renderStageFooter()}
                                </Picker.Footer>
                            )}
                        </Picker.List>
                    </Picker.Popup>
                </Picker.Select>
            </ViewTableCell>
        );
    },
);
